/**
 * Reconstructs TimelineItem[] from persisted `metadata.tool_results`.
 *
 * The backend stores completed tool events in each assistant message's metadata.
 * This function converts them back into the same TimelineItem shapes that the
 * WebSocket handler produces in real-time, so StreamingTimeline can render them
 * identically when reopening a conversation from history.
 *
 * Expects all entries to be PersistedToolResult (normalized upstream by
 * normalizeLegacyToolResult for pre-April 2026 conversations).
 */

import type { PersistedToolResult } from '@hector/shared-types/casediscovery';
import type { TimelineItem, ToolEvent } from '../types/session.types';
import type { JurisprudenceSource, FondementJuridique } from '../components/jurisprudence/types';
import type { ParsedJurisprudence } from '@/utils/jurisprudenceParser';

/** Module-level counter for unique IDs — never resets within a page session,
 *  combined with Date.now() to guarantee uniqueness across calls. */
let counter = 0;
function nextId(): string {
  return `restored-${++counter}-${Date.now()}`;
}

function restoreToolData(result: PersistedToolResult): Record<string, unknown> | undefined {
  const data: Record<string, unknown> = { ...result.input };

  // Le dossier de l'appel, tel que le serveur l'a décidé et gardé avec le
  // résultat. Il prime sur l'argument `case_id` du modèle, absent quand l'outil
  // a déduit le dossier (pièce lue par son identifiant) : sans lui, la ligne
  // d'outil cesse de nommer un connexe au rechargement. Posé avant les
  // reconstructions ci-dessous, dont le `case_id` propre à l'outil l'emporte.
  if (result.caseId) data.case_id = result.caseId;

  if (result.toolName === 'launch_case_wiki_analysis') {
    const persisted = result.result;

    // The persisted tool result uses the launch domain contract (camelCase),
    // while live backend_tool frames use snake_case. Rebuild the wire shape so
    // reopening a scheduled conversation cannot make it look launched.
    if (typeof persisted.caseId === 'string') data.case_id = persisted.caseId;
    if (typeof persisted.status === 'string') data.status = persisted.status;
    if (typeof persisted.jobId === 'string') data.job_id = persisted.jobId;
    if (typeof persisted.dispatchRequestId === 'string') {
      data.dispatch_request_id = persisted.dispatchRequestId;
    }
    if (typeof persisted.scheduledFor === 'string') {
      data.scheduled_for = persisted.scheduledFor;
    }
    if (typeof persisted.totalPieces === 'number') data.total_pieces = persisted.totalPieces;
  } else if (result.toolName === 'create_case') {
    const persisted = result.result;

    // Le résultat rendu au modèle est en camelCase, la trame `backend_tool` du
    // direct en snake_case. Sans `caseId`, aucun dossier n'a été rendu (refus,
    // échec) : on n'invente rien, et surtout on ne garde pas le `case_id` de
    // l'appel, qui est celui d'un autre dossier.
    delete data.case_id;
    if (typeof persisted.caseId === 'string') {
      data.case_id = persisted.caseId;
      if (persisted.existing === true) data.existing = true;
    }
  } else if (result.toolName === 'open_objective' || result.toolName === 'close_objective') {
    // Objective lifecycle events deliberately expose both the model declaration
    // (input) and the registry verdict (result). Without this merge, a reload
    // loses `pending` and makes an unfinished objective look successful.
    Object.assign(data, result.result);
  }

  return Object.keys(data).length > 0 ? data : undefined;
}

/**
 * Maps the raw `result` payload of a search_jurisprudence tool call into
 * the fields expected by a `jurisprudence_results` TimelineItem.
 *
 * Shared between the JP branch and any future caller that needs the same
 * snake_case → camelCase mapping for persisted JP data.
 */
function mapJpResult(d: Record<string, unknown>): {
  jurisprudences: ParsedJurisprudence[];
  sources: JurisprudenceSource[];
  responseText: string;
  jobId: string;
  fondements: FondementJuridique[];
} {
  // NOTE réhydratation : les vieux blobs persistés (ère quick/deep, retirée
  // 2026-07) peuvent encore porter `sub_questions`, `searchDepth`/`search_depth`
  // et `mini_response` — ces champs sont ignorés silencieusement ici.
  return {
    jobId: (d.jobId as string) ?? 'legacy',
    responseText: (d.response_text as string) ?? '',
    sources: (d.sources ?? []) as JurisprudenceSource[],
    jurisprudences: ((d.jurisprudences ?? []) as Record<string, unknown>[]).map((jp) => ({
      id: jp.id as string,
      title: jp.title as string,
      reference: jp.reference as string,
      officialSummary: jp.official_summary as string | undefined,
      principles: jp.principles as string | undefined,
      context: jp.context as string | undefined,
      dispositif: jp.dispositif as string | undefined,
      relevance: undefined as string | undefined,
      relatedJurisprudences: (jp.related_jurisprudences as string) ?? '',
      citedArticles: (jp.cited_articles as string) ?? '',
      sourceQuestions: jp.source_questions as string[] | undefined,
      visa: jp.visa as string | undefined,
      rapprochements: jp.rapprochements as string | undefined,
    })) as unknown as ParsedJurisprudence[],
    fondements: (d.fondements_used ?? []) as FondementJuridique[],
  };
}

/** Tool names des jobs de rédaction canvas — mappés en cartes `redaction_plan_results`. */
const REDACTION_JOB_TOOLS = new Set(['start_research_only_job', 'start_generation_only_job']);

/** `status` du tool_result backend (RedactionJobCollector) → `verdict` de la carte. */
const REDACTION_STATUS_TO_VERDICT: Record<string, Extract<TimelineItem, { type: 'redaction_plan_results' }>['verdict']> = {
  completed: 'completed',
  declined: 'declined',
  // Historical pre-#1481 rows remain readable without preserving the obsolete UI verdict.
  timeout: 'error',
  interrupted: 'interrupted',
  cancelled: 'cancelled',
  failed: 'error',
};

/**
 * Maps le tool_result persisté d'un job de rédaction (shape
 * `RedactionJobCollector.finishWith` — enrichi kind/jobId/progress_snapshot
 * depuis #568) vers l'item `redaction_plan_results` que la timeline live
 * produit au terminal. Le `progress_snapshot` (camelCase, aligné sur le shape
 * frontend par le backend) rend la carte réhydratée dépliable comme en live.
 */
function mapRedactionJobResult(
  toolResult: PersistedToolResult,
): Extract<TimelineItem, { type: 'redaction_plan_results' }> {
  const d = toolResult.result;
  const status = typeof d.status === 'string' ? d.status : 'completed';
  const kind = d.kind === 'generation' || toolResult.toolName === 'start_generation_only_job'
    ? 'generation' as const
    : 'research' as const;
  const snapshot = d.progress_snapshot;
  return {
    type: 'redaction_plan_results',
    id: nextId(),
    // Le requestId du collector EST le toolCallId déféré (nonce de corrélation).
    requestId: toolResult.toolCallId,
    jobId: typeof d.jobId === 'string' ? d.jobId : '',
    kind,
    verdict: toolResult.isError ? 'error' : (REDACTION_STATUS_TO_VERDICT[status] ?? 'error'),
    proposedPayload: {
      ...(typeof d.nom_acte === 'string' ? { nom_acte: d.nom_acte } : {}),
      ...(typeof d.specialisation === 'string' ? { specialisation: d.specialisation } : {}),
      ...(d.generation_mode === 'full' || d.generation_mode === 'section_i' || d.generation_mode === 'section_ii_iii'
        ? { generation_mode: d.generation_mode }
        : {}),
      // Un lancement en un seul temps est un job `'research'` qui écrit pourtant
      // l'acte : sans ce drapeau la carte relue retombe sur `kind` et réaffiche
      // « Plan rédigé » sur un acte complet (cf. `productionRedigee`). Le
      // backend le pose dans le résumé persisté depuis le 2026-08-30 ; les
      // jobs antérieurs ne l'ont pas, et restent donc étiquetés « plan ».
      ...(d.full_document === true ? { full_document: true } : {}),
    },
    extras: {
      ...(typeof d.arguments_count === 'number' ? { arguments_count: d.arguments_count } : {}),
      ...(typeof d.message === 'string' ? { message: d.message } : {}),
    },
    ...(snapshot && typeof snapshot === 'object'
      ? { progressSnapshot: snapshot as Extract<TimelineItem, { type: 'redaction_plan_results' }>['progressSnapshot'] }
      : {}),
  };
}

/**
 * Converts persisted PersistedToolResult[] into TimelineItem[] matching the live WS streaming shapes.
 *
 * For `search_jurisprudence`: produces `jurisprudence_results` items (cards, sources).
 * For redaction jobs (`start_research_only_job` / `start_generation_only_job`):
 * produces `redaction_plan_results` cards (verdict + snapshot dépliable, #568).
 * For all other tools: produces `tool` items rendered via ToolGroup (groupes de salves).
 */
export function buildTimelineFromToolResults(toolResults: PersistedToolResult[]): TimelineItem[] {
  const items: TimelineItem[] = [];

  for (const result of toolResults) {
    if (result.toolName === 'search_jurisprudence' && !result.isError) {
      const jp = mapJpResult(result.result);
      items.push({
        type: 'jurisprudence_results',
        id: nextId(),
        ...jp,
      });
    } else if (REDACTION_JOB_TOOLS.has(result.toolName)) {
      items.push(mapRedactionJobResult(result));
    } else {
      // All other backend tools (+ errored JP): restore as `tool` items (ToolGroup).
      // WHY `input` as `data`: the live WS event sends a small summary object
      // crafted by each tool executor. We don't have that here — only the raw
      // Claude tool input. This is enough for getInlineParams() (query, url,
      // article_id) but summary badges (chunks_count, decisions_found) won't
      // appear for restored tools. Acceptable trade-off vs persisting summary data.
      const event: ToolEvent = {
        id: nextId(),
        tool: result.toolName as ToolEvent['tool'],
        status: result.isError ? 'error' : 'completed',
        data: restoreToolData(result),
        timestamp: result.timestamp ?? new Date().toISOString(),
      };
      items.push({ type: 'tool', id: event.id, event });
    }
  }

  return items;
}
